import { apiClient } from '@/lib/api-client';
import type { ApiSuccess } from '@/lib/api-client';

import type { GetitPurchasePriceDto } from './types';

/**
 * Endpoints (backend/src/modules/getit_purchase_prices/routes.rs):
 *   GET  /api/monthly-batches/:batchId/getit-purchase-price  -> list
 *   POST /api/monthly-batches/:batchId/getit-purchase-price  -> create/recompute
 *   POST /api/getit-purchase-prices/:id/confirm              -> confirm
 *   POST /api/getit-purchase-prices/:id/lock                 -> lock
 *
 * Compute requires BOTH the batch's first valuation (B) and monthly material
 * valuation (A) to be locked, else the backend returns a Conflict error.
 */

/** Latest supplier purchase price for the batch (backend lists newest-first). */
export const fetchGetitPurchasePrice = async (
  batchId: string,
): Promise<GetitPurchasePriceDto | null> => {
  const res = await apiClient.get<ApiSuccess<GetitPurchasePriceDto[]>>(
    `/monthly-batches/${batchId}/getit-purchase-price`,
  );
  const rows = res.data.data ?? [];
  return rows[0] ?? null;
};

/** Create or recompute (idempotent) B/C/D. Gated by valuation.market_price.apply. */
export const recomputeGetitPurchasePrice = async (
  batchId: string,
): Promise<GetitPurchasePriceDto> => {
  const res = await apiClient.post<ApiSuccess<GetitPurchasePriceDto>>(
    `/monthly-batches/${batchId}/getit-purchase-price`,
  );
  return res.data.data;
};

/** Confirm the price. Gated by the backend quotation confirmation permission. */
export const confirmGetitPurchasePrice = async (id: string): Promise<GetitPurchasePriceDto> => {
  const res = await apiClient.post<ApiSuccess<GetitPurchasePriceDto>>(
    `/getit-purchase-prices/${id}/confirm`,
  );
  return res.data.data;
};

/** Lock the price (freeze B/C/D). Gated by freight.confirm. */
export const lockGetitPurchasePrice = async (id: string): Promise<GetitPurchasePriceDto> => {
  const res = await apiClient.post<ApiSuccess<GetitPurchasePriceDto>>(
    `/getit-purchase-prices/${id}/lock`,
  );
  return res.data.data;
};
